/**
 * MonthCard — one month of the Review Planner year grid.
 *
 * Open months (Feb–Nov) show the month name, a client-count badge
 * (accent-coloured when the month is heavily loaded), and a scrollable
 * FLAT alphabetical list of UNITS (CLAUDE.md §34.10): group blocks and
 * ungrouped client rows interleaved — the old Individuals / Companies
 * sections are gone. January and December render as closed cards:
 * dashed border, no list, never a drop target (§34.2).
 *
 * Groups: a bordered block with the group label (uppercase), a
 * member-count badge, a rename pencil, and every member row inside.
 * HARD RULE (§34.10): the block ALWAYS shows every member, fully — no
 * collapsing, no truncation, no "+N more". The month list may grow and
 * scroll; members are never hidden.
 *
 * Multi-select: row checkboxes appear on hover and stay visible while
 * any selection exists; the group header checkbox toggles all members.
 * Drag: ungrouped rows drag individually; a group block drags as ONE
 * unit (all members move together); member rows inside a group are NOT
 * individually draggable — ungroup first.
 */

import React, { useState } from 'react';
import { Check, Pencil, Users } from 'lucide-react';
import {
  MONTH_NAMES,
  isOpenMonth,
  applicableChecklistItems,
  buildMonthUnits,
  computeRelatedHints,
  formatClientListName,
  isReviewOverdue,
  getCurrentSASTYearMonth,
} from '../../utils/reviewPlanner';

// A month with this many clients or more gets the accent-coloured badge —
// a visual cue that the month is filling up.
const HIGH_COUNT_THRESHOLD = 12;

// Status set (§34.9): grey while planned, orange while on the pipeline
// (submitted or appointment made), green once completed.
const STATUS_DOT_COLOURS = {
  planned: 'var(--cp-text-muted)',
  submitted: 'var(--cp-accent)',
  appointment_made: 'var(--cp-accent)',
  completed: 'var(--cp-success)',
};

/**
 * Mini checklist squares: one small square per APPLICABLE item in fixed
 * AR / 14 / LA / CS order — filled with a check when done, hollow when
 * pending, absent when not applicable. Entities structurally show only
 * AR + CS (applicableChecklistItems, §34.10).
 */
function MiniChecklist({ client }) {
  return (
    <span className="rv-mini-checklist">
      {applicableChecklistItems(client).map((item) => {
        const done = client[item.doneField];
        return (
          <span
            key={item.key}
            className={`rv-mini-box${done ? ' rv-mini-done' : ''}`}
            title={`${item.label} — ${done ? 'done' : 'pending'}`}
          >
            {done && <Check size={9} strokeWidth={3.5} />}
          </span>
        );
      })}
    </span>
  );
}

function ClientRow({
  client,
  now,
  related,
  onOpen,
  draggable,
  onDragStart,
  onDragEnd,
  isDragging,
  selected,
  selectionMode,
  onToggleSelect,
}) {
  // Surname-first list rendering ("Botes ME (Magda)") so the
  // alphabetical order reads at a glance; stored names are untouched.
  const listName = formatClientListName(client);
  return (
    <div
      className={
        'rv-client-row' +
        (isDragging ? ' rv-dragging' : '') +
        (selectionMode ? ' rv-selecting' : '') +
        (selected ? ' rv-selected' : '')
      }
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart(e, client) : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      onClick={() => (selectionMode ? onToggleSelect(client.id) : onOpen(client))}
      title={listName}
    >
      <input
        type="checkbox"
        className="rv-row-check"
        checked={selected}
        onChange={() => onToggleSelect(client.id)}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select ${listName}`}
      />
      <span
        className="rv-status-dot"
        style={{ background: STATUS_DOT_COLOURS[client.status] || STATUS_DOT_COLOURS.planned }}
      />
      <span className="rv-client-name">{listName}</span>
      {client.isEntity && <span className="rv-tag rv-tag-entity">ENTITY</span>}
      {/* Render-time hint among ungrouped units only (§34.10). */}
      {related && <span className="rv-tag rv-tag-related">RELATED?</span>}
      {/* Derived at render time — never persisted (§34.4). */}
      {isReviewOverdue(client, now) && <span className="rv-tag rv-tag-overdue">OVERDUE</span>}
      <MiniChecklist client={client} />
    </div>
  );
}

function GroupBlock({
  unit,
  now,
  onOpenClient,
  onRenameGroup,
  onDragStartGroup,
  onDragEndGroup,
  isDragging,
  selectedIds,
  selectionMode,
  onToggleSelect,
  onToggleSelectGroup,
}) {
  const memberIds = unit.members.map((m) => m.id);
  const allSelected = memberIds.every((id) => selectedIds.has(id));

  return (
    <div
      className={
        'rv-group-block' +
        (isDragging ? ' rv-dragging' : '') +
        (selectionMode ? ' rv-selecting' : '')
      }
      draggable
      onDragStart={(e) => onDragStartGroup(e, unit)}
      onDragEnd={onDragEndGroup}
    >
      <div className="rv-group-head">
        <input
          type="checkbox"
          className="rv-row-check"
          checked={allSelected}
          onChange={() => onToggleSelectGroup(memberIds, !allSelected)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select all members of ${unit.label}`}
        />
        <Users size={11} className="rv-group-icon" />
        <span className="rv-group-label" title={unit.label}>{unit.label}</span>
        <span className="rv-group-count">{unit.members.length}</span>
        <button
          type="button"
          className="rv-group-rename"
          title="Rename group"
          onClick={(e) => {
            e.stopPropagation();
            onRenameGroup(unit);
          }}
        >
          <Pencil size={11} />
        </button>
      </div>
      {/* HARD RULE (§34.10): every member always rendered in full —
          never collapsed, never truncated, never "+N more". */}
      {unit.members.map((member) => (
        <ClientRow
          key={member.id}
          client={member}
          now={now}
          related={false}
          onOpen={onOpenClient}
          draggable={false}
          isDragging={false}
          selected={selectedIds.has(member.id)}
          selectionMode={selectionMode}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}

export default function MonthCard({
  month,
  clients,
  groupLabels = {},
  onOpenClient,
  onDropUnit, // ({ clientId } | { groupId }, month)
  onRenameGroup, // (unit) => void
  draggingKey, // 'client:<id>' | 'group:<id>' | null
  onDragStartUnit, // (event, key) => void
  onDragEndUnit,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectGroup, // (memberIds, select) => void
}) {
  const [dragOver, setDragOver] = useState(false);
  const open = isOpenMonth(month);
  // Current SAST year/month, computed once per card render and shared by
  // every row's derived-overdue check.
  const now = getCurrentSASTYearMonth();

  if (!open) {
    return (
      <div className="rv-month-card rv-closed">
        <div>
          <div className="rv-month-name">{MONTH_NAMES[month]}</div>
          <div className="rv-closed-note">
            Kept clear by design — no annual reviews scheduled
          </div>
        </div>
      </div>
    );
  }

  const units = buildMonthUnits(clients, groupLabels);
  const relatedIds = computeRelatedHints(clients);
  const selectionMode = selectedIds.size > 0;

  return (
    <div
      className={`rv-month-card${dragOver ? ' rv-drop-target' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const payload = e.dataTransfer.getData('text/plain');
        if (!payload) return;
        const [kind, id] = payload.split(':');
        if (kind === 'client') onDropUnit({ clientId: id }, month);
        if (kind === 'group') onDropUnit({ groupId: id }, month);
      }}
    >
      <div className="rv-month-head">
        <span className="rv-month-name">{MONTH_NAMES[month]}</span>
        {/* Counts CLIENTS, not units (§34.10) — unchanged. */}
        <span
          className={`rv-count-badge${clients.length >= HIGH_COUNT_THRESHOLD ? ' rv-count-high' : ''}`}
        >
          {clients.length}
        </span>
      </div>
      <div className="rv-client-list">
        {units.length === 0 ? (
          <div className="rv-month-empty">No reviews planned</div>
        ) : (
          units.map((unit) =>
            unit.type === 'group' ? (
              <GroupBlock
                key={`group:${unit.groupId}`}
                unit={unit}
                now={now}
                onOpenClient={onOpenClient}
                onRenameGroup={onRenameGroup}
                onDragStartGroup={(e) => onDragStartUnit(e, `group:${unit.groupId}`)}
                onDragEndGroup={onDragEndUnit}
                isDragging={draggingKey === `group:${unit.groupId}`}
                selectedIds={selectedIds}
                selectionMode={selectionMode}
                onToggleSelect={onToggleSelect}
                onToggleSelectGroup={onToggleSelectGroup}
              />
            ) : (
              <ClientRow
                key={unit.client.id}
                client={unit.client}
                now={now}
                related={relatedIds.has(unit.client.id)}
                onOpen={onOpenClient}
                draggable
                onDragStart={(e) => onDragStartUnit(e, `client:${unit.client.id}`)}
                onDragEnd={onDragEndUnit}
                isDragging={draggingKey === `client:${unit.client.id}`}
                selected={selectedIds.has(unit.client.id)}
                selectionMode={selectionMode}
                onToggleSelect={onToggleSelect}
              />
            )
          )
        )}
      </div>
    </div>
  );
}
